import i18n from '@/utils/i18n';
import { captureException } from '@/utils/sentry';
import {
  EmployeeNotifications,
  NotificationToggleField,
} from '@/experiences/sales-portal/domain/accounts/screens/employee-list/types';
import { useCallback, useState } from 'react';
import { useExperienceToasts } from '@experiences/hooks/use-experience-toasts';
import { useSalesPortalUpdateEmployeeNotificationsMutation } from '@/experiences/sales-portal/domain/accounts/screens/employee-list/containers/employee-notifications-section/blocs/documents/sales-portal-update-employee-notifications.generated';

interface UseUpdateEmployeeNotificationsMutatorResult {
  notifications: EmployeeNotifications;
  onChange: (field: NotificationToggleField, value: boolean) => Promise<void>;
  pendingField: NotificationToggleField | null;
}

/**
 * Mutator for toggling employee-notifications fields on/off.
 *
 * Holds notifications locally and optimistically applies each toggle before
 * firing the mutation. On failure it rolls the field back and toasts a
 * specific error.
 *
 *
 * @param initialNotifications - Notifications snapshot from the loader
 * @returns `{ notifications, onChange, pendingField }` —
 *   `notifications` reflects the current value;
 *   `onChange(field, value)` fires the mutation;
 *   `pendingField` is the field currently being updated (or null when idle).
 */
export const useUpdateEmployeeNotificationsMutator = (
  initialNotifications: EmployeeNotifications,
): UseUpdateEmployeeNotificationsMutatorResult => {
  const { showErrorToast } = useExperienceToasts();
  const [updateNotifications] =
    useSalesPortalUpdateEmployeeNotificationsMutation();
  const [notifications, setNotifications] = useState(initialNotifications);
  const [pendingField, setPendingField] =
    useState<NotificationToggleField | null>(null);

  const onChange = useCallback(
    async (field: NotificationToggleField, value: boolean) => {
      const previous = notifications[field];
      setNotifications({ ...notifications, [field]: value });
      setPendingField(field);
      try {
        const { data } = await updateNotifications({
          variables: {
            employeeContactInfoUUID: notifications.uuid,
            set: { [field]: value },
          },
        });
        if (!data?.update_employee_contact_info_by_pk) {
          captureException(
            new Error('update_employee_contact_info_by_pk returned null'),
            { extra: { response: data?.update_employee_contact_info_by_pk } },
          );
          setNotifications((prev) =>
            prev ? { ...prev, [field]: previous } : prev,
          );
          showErrorToast(
            i18n.t(
              'sales_portal.employee_edit_drawer.notifications.update_error',
            ),
          );
        }
      } catch (error) {
        captureException(error);
        setNotifications((prev) =>
          prev ? { ...prev, [field]: previous } : prev,
        );
        showErrorToast(
          i18n.t(
            'sales_portal.employee_edit_drawer.notifications.update_error',
          ),
        );
      } finally {
        // stop loading
        setPendingField((current) => (current === field ? null : current));
      }
    },
    [notifications, showErrorToast, updateNotifications],
  );

  return { notifications, onChange, pendingField };
};
